import Box from '@amzn/awsui-components-react/polaris/box';
import ColumnLayout from '@amzn/awsui-components-react/polaris/column-layout';
import Container from '@amzn/awsui-components-react/polaris/container';
import Form from '@amzn/awsui-components-react/polaris/form';
import Header from '@amzn/awsui-components-react/polaris/header';
import Link from '@amzn/awsui-components-react/polaris/link';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import TextContent from '@amzn/awsui-components-react/polaris/text-content';
import {
    CheckboxField,
    FormikField,
    ValueWithLabel,
} from '@amzn/event-engine-js-utils';
import { format } from 'date-fns';
import { FormikContextType, useFormikContext } from 'formik';
import React, { useEffect } from 'react';

import {
    FormSchemaType,
    Step,
    StepValidator,
} from './../../JoinEvent.validation';

export const isStepTwoValid: StepValidator = async (
    formik
): Promise<boolean> => {
    try {
        const validatedFormErrors = await formik.validateForm(formik.values);
        return !validatedFormErrors[Step.REVIEW_STEP_TWO];
    } catch (e) {
        return false;
    }
};

export const generateFormFieldPrefix = (
    str: keyof FormSchemaType[Step.REVIEW_STEP_TWO]
) => {
    return `${[
        Step.REVIEW_STEP_TWO,
    ]}.${str}` as keyof FormSchemaType[Step.REVIEW_STEP_TWO];
};

interface ReviewAndJoinProps {
    isValidating?: boolean;
}

const ReviewAndJoin = ({ isValidating }: ReviewAndJoinProps) => {
    const formik: FormikContextType<FormSchemaType> = useFormikContext();

    useEffect(() => {
        formik.setErrors({});

        return () => {
            formik.setFieldValue(
                generateFormFieldPrefix('termsAndConditions'),
                false
            );
        };
    }, []);

    return (
        <SpaceBetween size="l">
            <Container
                header={<Header variant="h2">Event details</Header>}
                footer={
                    <Box padding={{ vertical: 'xs' }}>
                        <ValueWithLabel label="Description">
                            {formik.values[Step.REVIEW_STEP_TWO].description}
                        </ValueWithLabel>
                    </Box>
                }>
                <ColumnLayout columns={4} variant="text-grid">
                    <ValueWithLabel label="Name">
                        {formik.values[Step.REVIEW_STEP_TWO].eventName}
                    </ValueWithLabel>
                    <ValueWithLabel label="Start time">
                        {format(
                            new Date(
                                formik.values[Step.REVIEW_STEP_TWO].startTime
                            ),
                            'M/dd/yyyy hh:mm a'
                        )}
                    </ValueWithLabel>
                    <ValueWithLabel label="Duration">
                        {formik.values[Step.REVIEW_STEP_TWO].duration}{' '}
                        {formik.values[Step.REVIEW_STEP_TWO].duration === 1
                            ? 'hour'
                            : 'hours'}
                    </ValueWithLabel>
                    <ValueWithLabel label="Level">
                        {formik.values[Step.REVIEW_STEP_TWO].level}
                    </ValueWithLabel>
                </ColumnLayout>
            </Container>
            <Form>
                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Read and accept before joining the event">
                            Terms and Conditions
                        </Header>
                    }>
                    <SpaceBetween size="l">
                        <TextContent>
                            <ol>
                                <li>
                                    By using AWS Workshop Studio for the
                                    relevant event, you agree to the{' '}
                                    <Link
                                        href="https://aws.amazon.com/events/terms"
                                        external>
                                        AWS Event Terms and Conditions
                                    </Link>{' '}
                                    and the{' '}
                                    <Link
                                        href="https://aws.amazon.com/aup"
                                        external>
                                        AWS Acceptable Use Policy
                                    </Link>
                                    . You acknowledge and agree that are using
                                    an AWS-owned account that you can only
                                    access for the duration of the relevant
                                    event. If you find residual resources or
                                    materials in the AWS-owned account, you will
                                    make us aware and cease use of the account.
                                    AWS reserves the right to terminate the
                                    account and delete the contents at any time.
                                </li>
                                <li>
                                    You will not: (a) process or run any
                                    operation on any data other than test data
                                    sets or lab-approved materials by AWS, and
                                    (b) copy, import, export or otherwise create
                                    derivate works of materials provided by AWS,
                                    including but not limited to, data sets.
                                </li>
                                <li>
                                    AWS is under no obligation to enable the
                                    transmission of your materials through AWS
                                    Workshop Studio and may, in its discretion,
                                    edit, block, refuse to post, or remove your
                                    materials at any time.
                                </li>
                                <li>
                                    Your use of AWS Workshop Studio will comply
                                    with these terms and all applicable laws,
                                    and your access to AWS Workshop Studio will
                                    immediately and automatically terminate if
                                    you do not comply with any of these terms or
                                    conditions.
                                </li>
                            </ol>
                        </TextContent>
                        <FormikField<FormSchemaType[Step.REVIEW_STEP_TWO]>
                            name={generateFormFieldPrefix('termsAndConditions')}
                            formFieldProps={{
                                stretch: true,
                                children: (
                                    <CheckboxField<
                                        FormSchemaType[Step.REVIEW_STEP_TWO]
                                    >
                                        name={generateFormFieldPrefix(
                                            'termsAndConditions'
                                        )}
                                        checkboxProps={{
                                            children:
                                                'I agree with the Terms and Conditions',
                                            disabled: isValidating,
                                        }}
                                    />
                                ),
                            }}
                        />
                    </SpaceBetween>
                </Container>
            </Form>
        </SpaceBetween>
    );
};

export default ReviewAndJoin;
